/**
 * Account Entry Page - /app/account
 * 
 * This page is the entry point for mobile app WebView.
 * It validates the token and either:
 * - Shows the Login page if token is valid
 * - Shows Access Denied if token is invalid/missing
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldX, Smartphone, Loader2 } from 'lucide-react';
import axios from 'axios';
import AccountLogin from './AccountLogin';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const AccountEntry = () => {
  const [searchParams] = useSearchParams();
  
  const [isLoading, setIsLoading] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateToken = async () => {
    const token = searchParams.get('token');
    
    // Check 1: Token must exist in URL
    if (!token) {
      setIsLoading(false);
      setError('No access token provided');
      return;
    }

    // Check 2: Basic token format validation (base64)
    try {
      // URL-safe base64 decode check
      const normalizedToken = token.replace(/-/g, '+').replace(/_/g, '/');
      atob(normalizedToken);
    } catch (e) {
      setIsLoading(false);
      setError('Invalid token format');
      return;
    }

    // Check 3: Validate with backend API
    try {
      const response = await axios.post(`${API_BASE_URL}/webview/validate-token`, {
        token: token
      });

      if (response.data.valid) {
        setIsValid(true);
        
        // Get target page and category from URL (default values)
        const target = searchParams.get('target') || 'account';
        const category = searchParams.get('category') || 'property_management';
        
        setUserData({
          userId: response.data.user_id,
          userType: response.data.user_type,
          businessId: response.data.business_id,
          email: response.data.email,
          name: response.data.name,
          targetPage: response.data.target_page || target,
          category: response.data.category || category
        });
        
        // Store token, target and category for session
        sessionStorage.setItem('webview_token', token);
        sessionStorage.setItem('webview_user', JSON.stringify(response.data));
        sessionStorage.setItem('webview_target', response.data.target_page || target);
        sessionStorage.setItem('webview_category', response.data.category || category);
        sessionStorage.setItem('from_app', 'true');
      } else {
        setError(response.data.error || 'Token validation failed');
      }
    } catch (err) {
      console.error('Token validation error:', err);
      setError('Unable to validate access token');
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-accent animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Validating access...</p>
        </div>
      </div>
    );
  }

  // Access Denied state
  if (!isValid) {
    return <AccessDenied error={error} />;
  }

  // Valid token - show login page with target and category
  return <AccountLogin userData={userData} targetPage={userData?.targetPage || 'account'} category={userData?.category} />;
};

// Access Denied Component
const AccessDenied = ({ error }) => {
  const playStoreUrl = process.env.REACT_APP_BUSINESS_APP_PLAYSTORE_URL || '#';
  const appStoreUrl = process.env.REACT_APP_BUSINESS_APP_APPSTORE_URL || '#';

  return (
    <div className="min-h-screen bg-dark flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full text-center"
      >
        {/* Icon */}
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
          <ShieldX className="w-10 h-10 text-red-500" />
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-white mb-3">Access Denied</h1>
        
        {/* Message */}
        <p className="text-gray-400 mb-6">
          This page can only be accessed from the EzDaily mobile app.
        </p>

        {/* Error details (optional, for debugging) */}
        {error && (
          <p className="text-sm text-red-400 mb-6 p-3 bg-red-500/10 rounded-lg">
            {error}
          </p>
        )}

        {/* App download section */}
        <div className="bg-dark-100 rounded-2xl p-6 border border-white/10">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Smartphone className="w-5 h-5 text-accent" />
            <span className="text-white font-medium">Download our app</span>
          </div>
          
          <p className="text-gray-500 text-sm mb-4">
            Access your account from the EzDaily Business app
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={playStoreUrl}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-dark-200 rounded-lg border border-white/10 hover:border-accent/50 transition-colors"
            >
              <svg className="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" />
              </svg>
              <span className="text-white text-sm">Google Play</span>
            </a>
            
            <a
              href={appStoreUrl}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-dark-200 rounded-lg border border-white/10 hover:border-accent/50 transition-colors"
            >
              <svg className="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              <span className="text-white text-sm">App Store</span>
            </a>
          </div>
        </div>

        {/* Go back link */}
        <a
          href="/"
          className="inline-block mt-6 text-accent hover:text-accent-light transition-colors"
        >
          ← Go to Homepage
        </a>
      </motion.div>
    </div>
  );
};

export default AccountEntry;
